import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import type { AircraftTypeShort } from "src/shared/api-types/aircraft";
import type { AssignedLimitationInput } from "src/shared/api-types/limitations";

import { WithPermission } from "src/components/Authorization";
import ExpandCollapseButton from "src/components/ExpandCollapseButton";
import TextField from "src/components/TextField";
import Spinner from "src/components/Spinner";
import { PERMISSIONS } from "src/constants";
import { useLimitations } from "src/features/limitations/hooks/useLimitations";

import { useAircraftOperations, getOperationState, toLimitationInputs } from "./hooks/useAircraftOperations";
import AircraftSectionComponent from "./components/AircraftSection";
import type { AircraftSection, LinkedOperation } from "./types";

interface AircraftOperationsSectionProps {
  assignedAircraft: AircraftTypeShort[];
  crewId: number;
  userId: number;
  onUnassign?: (typeId: number) => void;
  aircraftSearchTerm: string;
  onAircraftSearchChange: (value: string) => void;
  canEdit: boolean;
  onLoadingChange?: (loading: boolean) => void;
  showLoadingSpinner?: boolean;
}

const AircraftOperationsSection: React.FC<AircraftOperationsSectionProps> = ({
  assignedAircraft,
  crewId,
  userId,
  onUnassign,
  aircraftSearchTerm,
  onAircraftSearchChange,
  canEdit,
  onLoadingChange,
  showLoadingSpinner = true,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { states, operationNames, isLoading: operationsLoading, update } = useAircraftOperations(crewId, userId);
  const { limitations: limitationDefinitions, isLoading: limitationsLoading } = useLimitations(crewId, {
    enabled: canEdit,
  });
  const [operationSearchTerm, setOperationSearchTerm] = useState("");
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  const savedExpanded = useRef<Record<number, boolean> | null>(null);
  const expandedSectionsRef = useRef(expandedSections);
  expandedSectionsRef.current = expandedSections;
  const isLoading = operationsLoading || limitationsLoading;

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const filteredAircraft = useMemo(() => {
    if (!aircraftSearchTerm) return assignedAircraft;
    const term = aircraftSearchTerm.toLowerCase();
    return assignedAircraft.filter(
      (a) => a.typeName?.toLowerCase().includes(term) || a.designatorName?.toLowerCase().includes(term),
    );
  }, [assignedAircraft, aircraftSearchTerm]);

  useEffect(() => {
    if (operationSearchTerm) {
      if (savedExpanded.current === null) {
        savedExpanded.current = { ...expandedSectionsRef.current };
      }
      const allExpanded: Record<number, boolean> = {};
      assignedAircraft.forEach((a) => {
        allExpanded[a.typeId] = true;
      });
      setExpandedSections(allExpanded);
    } else if (savedExpanded.current !== null) {
      setExpandedSections(savedExpanded.current);
      savedExpanded.current = null;
    }
  }, [operationSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = useMemo<AircraftSection[]>(() => {
    return filteredAircraft.map((aircraftType) => {
      const uniqueOpIds = Array.from(
        new Set(
          Object.values(states)
            .filter((s) => s.typeId === aircraftType.typeId)
            .map((s) => s.operationId),
        ),
      );

      const operations: LinkedOperation[] = uniqueOpIds
        .map((operationId) => {
          const state = getOperationState(states, aircraftType.typeId, operationId);
          return {
            operationId,
            operationName: operationNames[operationId] ?? String(operationId),
            typeId: aircraftType.typeId,
            enabled: state.enabled,
            underSupervision: state.underSupervision,
            limitations: state.limitations,
            saving: state.saving,
          };
        })
        .filter(
          (op) => !operationSearchTerm || op.operationName.toLowerCase().includes(operationSearchTerm.toLowerCase()),
        )
        .sort((a, b) => a.operationName.localeCompare(b.operationName));

      return { aircraftType, operations };
    });
  }, [filteredAircraft, operationNames, states, operationSearchTerm]);

  const handleToggle = useCallback(
    (
      typeId: number,
      operationId: number,
      enabled: boolean,
      underSupervision: boolean,
      limitations?: AssignedLimitationInput[],
    ) => {
      return update(typeId, operationId, enabled, underSupervision, limitations);
    },
    [update],
  );

  const handleCopy = useCallback(
    async (sourceOperation: LinkedOperation, allOperations: LinkedOperation[]) => {
      for (const op of allOperations) {
        if (op.operationId === sourceOperation.operationId) continue;
        await update(
          op.typeId,
          op.operationId,
          sourceOperation.enabled,
          sourceOperation.underSupervision,
          toLimitationInputs(sourceOperation.limitations),
          t("aircraft_management.copy_history_comment", { operation: sourceOperation.operationName }),
        );
      }
    },
    [t, update],
  );

  const sectionTypeIds = useMemo(() => sections.map((section) => section.aircraftType.typeId), [sections]);
  const hasAnyExpanded = sectionTypeIds.some((typeId) => expandedSections[typeId]);

  const handleToggleAll = useCallback(() => {
    const nextExpanded: Record<number, boolean> = {};
    sectionTypeIds.forEach((typeId) => {
      nextExpanded[typeId] = !hasAnyExpanded;
    });
    setExpandedSections((prev) => ({ ...prev, ...nextExpanded }));
    savedExpanded.current = null;
  }, [hasAnyExpanded, sectionTypeIds]);

  if (isLoading) {
    if (!showLoadingSpinner) return null;
    return <Spinner message={t("loading")} />;
  }

  if (assignedAircraft.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("compliance.no_aircraft_assigned")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          justifyContent: "space-between",
          mb: 1,
          gap: 1,
          minWidth: 0,
        }}
      >
        <Typography variant="h6" sx={{ whiteSpace: "nowrap", flexShrink: 0, lineHeight: "40px" }}>
          {t("aircraft_management.operations_section_title")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: { sm: "wrap" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: { sm: "flex-end" },
            gap: 1,
            minWidth: 0,
            flex: "1 1 auto",
          }}
        >
          <WithPermission permission={PERMISSIONS.BULK_UPLOAD_CERTIFICATES}>
            <Button
              variant="outlined"
              onClick={() => history.push(`/crew/${crewId}/bulk-upload/user-courses?userId=${userId}`)}
              aria-label={String(t("aircraft_management.bulk_upload_certificates"))}
              sx={{
                width: { xs: "100%", sm: 40, md: "auto" },
                height: 40,
                minWidth: { xs: 64, sm: 40, md: 64 },
                justifyContent: { xs: "flex-start", sm: "center", md: "flex-start" },
                px: { xs: 2, sm: 1, md: 2 },
                flex: { xs: "1 1 auto", sm: "0 0 auto" },
              }}
            >
              <Box component="span" sx={{ width: 20, display: "inline-flex", flexShrink: 0, justifyContent: "center" }}>
                <UploadFileIcon fontSize="small" />
              </Box>
              <Box component="span" sx={{ ml: 1, display: { xs: "inline", sm: "none", md: "inline" } }}>
                {t("aircraft_management.bulk_upload_certificates")}
              </Box>
            </Button>
          </WithPermission>
          <TextField
            size="small"
            label={t("aircraft_management.search_aircraft")}
            value={aircraftSearchTerm}
            onChange={(e) => onAircraftSearchChange(e.target.value)}
            variant="outlined"
            sx={{ width: { xs: "100%", sm: 160 }, flex: { sm: "0 1 180px" } }}
            clearable
            onClear={() => onAircraftSearchChange("")}
          />
          <TextField
            size="small"
            label={t("aircraft_management.search_operations")}
            value={operationSearchTerm}
            onChange={(e) => setOperationSearchTerm(e.target.value)}
            variant="outlined"
            sx={{ width: { xs: "100%", sm: 160 }, flex: { sm: "0 1 180px" } }}
            clearable
            onClear={() => setOperationSearchTerm("")}
          />
          <ExpandCollapseButton expanded={hasAnyExpanded} onToggle={handleToggleAll} type="aircraft" />
        </Box>
      </Box>
      {sections.map((section) => (
        <AircraftSectionComponent
          key={section.aircraftType.typeId}
          section={section}
          userId={userId}
          crewId={crewId}
          isFiltered={!!operationSearchTerm}
          canEdit={canEdit}
          limitationDefinitions={limitationDefinitions}
          onToggle={handleToggle}
          onCopy={handleCopy}
          onUnassign={onUnassign}
          expanded={expandedSections[section.aircraftType.typeId] ?? false}
          onExpandChange={(val) => setExpandedSections((prev) => ({ ...prev, [section.aircraftType.typeId]: val }))}
        />
      ))}
    </Box>
  );
};

export default AircraftOperationsSection;
